import * as React from 'react';
import { useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  alpha,
  AppBar,
  Toolbar,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Divider,
  Chip,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  keyframes,
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { PodcastIcon } from '../../components/icons/PodcastIcon';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import SpeedIcon from '@mui/icons-material/Speed';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

import { inputsCustomizations } from '../../shared-theme/customizations/inputs';
import { dataDisplayCustomizations } from '../../shared-theme/customizations/dataDisplay';
import { feedbackCustomizations } from '../../shared-theme/customizations/feedback';
import { navigationCustomizations } from '../../shared-theme/customizations/navigation';
import { brand } from '../../shared-theme/themePrimitives';
import { surfacesCustomizations } from '../../shared-theme/customizations/surfaces';
import { colorSchemes, typography, shadows, shape } from '../../shared-theme/themePrimitives';

// ===================== KEYFRAME ANIMATIONS =====================

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-8px) rotate(2deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// ===================== HELPERS =====================

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }
};

// ===================== ANIMATED COMPONENTS =====================

function useIntersection(options?: IntersectionObserverInit) {
  const [isVisible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) setVisible(true); }),
      { threshold: 0.1, ...options }
    );
    const current = ref.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, [options]);

  return { ref, isVisible };
}

function FadeIn({ children, delay = 0, direction = 'up' }: {
  children: React.ReactNode;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
}) {
  const { ref, isVisible } = useIntersection();

  const transforms: Record<string, string> = {
    up: 'translateY(40px)',
    down: 'translateY(-40px)',
    left: 'translateX(40px)',
    right: 'translateX(-40px)',
    none: 'none',
  };

  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'none' : transforms[direction],
        transition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
        willChange: 'opacity, transform',
        width: '100%',
      }}
    >
      {children}
    </div>
  );
}

function AnimatedCounter({ value, suffix = '' }: { value: string; suffix?: string }) {
  const [display, setDisplay] = useState('0');
  const { ref, isVisible } = useIntersection({ threshold: 0.5 });

  useEffect(() => {
    if (!isVisible) return;
    const numericPart = parseFloat(value.replace(/[^0-9.]/g, ''));
    const duration = 2000;
    const steps = 60;
    let step = 0;
    const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);
    const timer = setInterval(() => {
      step++;
      const progress = easeOutQuart(step / steps);
      const current = numericPart * progress;
      if (Number.isInteger(numericPart)) {
        setDisplay(Math.round(current).toLocaleString());
      } else {
        setDisplay(current.toFixed(1));
      }
      if (step >= steps) {
        setDisplay(value.replace(/[^0-9.,]/g, ''));
        clearInterval(timer);
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, value]);

  return <span ref={ref as any}>{display}{suffix}</span>;
}

// Parallax wrapper
function useParallax(speed = 0.5) {
  const [offset, setOffset] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const elementCenter = rect.top + rect.height / 2;
    const distanceFromCenter = elementCenter - windowHeight / 2;
    setOffset(distanceFromCenter * speed * -0.1);
  }, [speed]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return { ref, offset };
}

// ===================== MAIN CONTENT =====================

function LandingPageContent() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const heroParallax = useParallax(0.3);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (path: string) => {
    const isApp = window.location.hostname.startsWith('app.');
    if (!isApp && window.location.hostname !== 'localhost') {
      window.open(`https://app.podstack.co${path}`, '_blank');
    } else {
      navigate(path);
    }
  };

  const navItems = ['Features', 'Stats', 'Pricing'];

  return (
    <Box sx={{ bgcolor: '#FAFAFA', minHeight: '100vh', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>

      {/* ==================== NAVBAR ==================== */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          borderBottom: scrolled ? '1px solid' : '1px solid transparent',
          borderColor: scrolled ? alpha('#000', 0.06) : 'transparent',
          backdropFilter: 'blur(24px) saturate(180%)',
          backgroundColor: scrolled ? 'rgba(255,255,255,0.85)' : 'transparent',
          transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: 72 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ cursor: 'pointer', transition: 'opacity 0.2s', '&:hover': { opacity: 0.8 } }}
              onClick={() => scrollToSection('hero')}
            >
              <PodcastIcon size={30} color={brand[400]} />
              <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.5px', color: '#0F172A' }}>
                Podstack
              </Typography>
            </Stack>

            {/* Desktop nav */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
              {navItems.map((item) => (
                <Button
                  key={item}
                  variant="text"
                  size="small"
                  sx={{
                    color: '#64748B',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 2,
                    py: 0.75,
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    '&:hover': { color: '#0F172A', bgcolor: alpha('#000', 0.04) },
                  }}
                  onClick={() => scrollToSection(item.toLowerCase())}
                >
                  {item}
                </Button>
              ))}
              <Divider orientation="vertical" flexItem sx={{ mx: 1.5, opacity: 0.5 }} />
              <Button
                variant="text"
                sx={{ color: '#475569', fontWeight: 600, textTransform: 'none', borderRadius: '8px', px: 2 }}
                onClick={() => handleNav('/login')}
              >
                Log in
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={() => handleNav('/signup')}
                sx={{
                  borderRadius: '10px',
                  fontWeight: 700,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  bgcolor: '#0F172A',
                  color: 'white',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: '#1E293B', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(15,23,42,0.2)' },
                }}
              >
                Get Started Free
              </Button>
            </Box>

            {/* Mobile hamburger */}
            <IconButton sx={{ display: { md: 'none' } }} onClick={() => setMobileMenuOpen(true)}>
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{ sx: { width: 300, p: 3 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <IconButton onClick={() => setMobileMenuOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <List>
          {navItems.map((item) => (
            <ListItem key={item} disablePadding>
              <ListItemButton
                sx={{ borderRadius: '8px', mb: 0.5 }}
                onClick={() => { scrollToSection(item.toLowerCase()); setMobileMenuOpen(false); }}
              >
                <ListItemText primary={item} primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItemButton>
            </ListItem>
          ))}
          <Divider sx={{ my: 2 }} />
          <ListItem disablePadding>
            <ListItemButton sx={{ borderRadius: '8px' }} onClick={() => { handleNav('/login'); setMobileMenuOpen(false); }}>
              <ListItemText primary="Log in" primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { handleNav('/signup'); setMobileMenuOpen(false); }}
              sx={{
                borderRadius: '12px', fontWeight: 700, py: 1.5, textTransform: 'none',
                bgcolor: '#0F172A', '&:hover': { bgcolor: '#1E293B' },
              }}
            >
              Get Started Free
            </Button>
          </ListItem>
        </List>
      </Drawer>

      {/* ==================== HERO ==================== */}
      <Box
        id="hero"
        ref={heroParallax.ref}
        sx={{
          position: 'relative',
          pt: { xs: 10, md: 16 },
          pb: { xs: 10, md: 20 },
          overflow: 'hidden',
        }}
      >
        {/* Animated gradient orbs */}
        <Box sx={{
          position: 'absolute', top: '-15%', right: '-8%', width: 700, height: 700,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(brand[200], 0.35)} 0%, transparent 65%)`,
          animation: `${pulse} 6s ease-in-out infinite`,
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '-5%', left: '-8%', width: 500, height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha('#7C3AED', 0.1)} 0%, transparent 65%)`,
          animation: `${pulse} 8s ease-in-out infinite 1s`,
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', top: '30%', left: '15%', width: 300, height: 300,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha('#3B82F6', 0.06)} 0%, transparent 65%)`,
          animation: `${pulse} 7s ease-in-out infinite 2s`,
          pointerEvents: 'none',
        }} />

        {/* Floating decorative elements */}
        <Box sx={{
          position: 'absolute', top: '15%', right: '12%',
          width: 60, height: 60, borderRadius: '16px',
          bgcolor: alpha(brand[400], 0.06),
          border: `1px solid ${alpha(brand[400], 0.1)}`,
          animation: `${float} 5s ease-in-out infinite`,
          display: { xs: 'none', lg: 'flex' }, alignItems: 'center', justifyContent: 'center',
        }}>
          <SearchIcon sx={{ color: alpha(brand[400], 0.4), fontSize: 24 }} />
        </Box>
        <Box sx={{
          position: 'absolute', top: '35%', left: '8%',
          width: 50, height: 50, borderRadius: '14px',
          bgcolor: alpha('#10B981', 0.06),
          border: `1px solid ${alpha('#10B981', 0.1)}`,
          animation: `${floatSlow} 6s ease-in-out infinite 0.5s`,
          display: { xs: 'none', lg: 'flex' }, alignItems: 'center', justifyContent: 'center',
        }}>
          <EmailIcon sx={{ color: alpha('#10B981', 0.4), fontSize: 20 }} />
        </Box>
        <Box sx={{
          position: 'absolute', bottom: '20%', right: '18%',
          width: 44, height: 44, borderRadius: '12px',
          bgcolor: alpha('#7C3AED', 0.06),
          border: `1px solid ${alpha('#7C3AED', 0.1)}`,
          animation: `${float} 7s ease-in-out infinite 1s`,
          display: { xs: 'none', lg: 'flex' }, alignItems: 'center', justifyContent: 'center',
        }}>
          <AutoAwesomeIcon sx={{ color: alpha('#7C3AED', 0.4), fontSize: 18 }} />
        </Box>

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ transform: `translateY(${heroParallax.offset}px)`, transition: 'transform 0.1s linear' }}>
            <FadeIn delay={100}>
              <Stack spacing={4} alignItems="center" textAlign="center" sx={{ maxWidth: 820, mx: 'auto' }}>
                {/* Badge */}
                <Box
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 1,
                    bgcolor: alpha(brand[400], 0.06),
                    border: `1px solid ${alpha(brand[400], 0.15)}`,
                    borderRadius: '100px',
                    px: 2.5, py: 0.75,
                    animation: `${scaleIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both`,
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: brand[400], animation: `${pulse} 2s ease-in-out infinite` }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.825rem', color: brand[500] }}>
                    Trusted by 4,000+ PR professionals
                  </Typography>
                </Box>

                {/* Main headline */}
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.75rem', sm: '3.75rem', md: '4.5rem' },
                    fontWeight: 800,
                    lineHeight: 1.05,
                    letterSpacing: '-0.04em',
                    color: '#0F172A',
                    animation: `${slideUp} 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both`,
                  }}
                >
                  Find the perfect podcast.{' '}
                  <Box
                    component="span"
                    sx={{
                      backgroundImage: `linear-gradient(90deg, ${brand[400]}, ${brand[300]}, #7C3AED, ${brand[400]})`,
                      backgroundSize: '200% auto',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      animation: `${shimmer} 4s linear infinite`,
                    }}
                  >
                    Book the interview.
                  </Box>
                </Typography>

                {/* Subheadline */}
                <Typography
                  variant="h6"
                  sx={{
                    color: '#64748B',
                    maxWidth: 640,
                    lineHeight: 1.75,
                    fontWeight: 400,
                    fontSize: { xs: '1rem', md: '1.2rem' },
                    animation: `${slideUp} 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s both`,
                  }}
                >
                  The all-in-one podcast outreach platform. Search 4M+ shows, discover verified host
                  emails, and send pitches that actually get replies.
                </Typography>

                {/* CTA buttons */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{
                    pt: 1,
                    animation: `${slideUp} 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.7s both`,
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    disableElevation
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleNav('/signup')}
                    sx={{
                      borderRadius: '14px',
                      px: 5,
                      py: 2,
                      fontSize: '1.05rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      bgcolor: '#0F172A',
                      color: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(15,23,42,0.12)',
                      '&:hover': {
                        bgcolor: '#1E293B',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 16px 40px rgba(15,23,42,0.18)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  >
                    Start for Free
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<PlayArrowRoundedIcon />}
                    onClick={() => scrollToSection('features')}
                    sx={{
                      borderRadius: '14px',
                      px: 4,
                      py: 2,
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: '#E2E8F0',
                      borderWidth: '1.5px',
                      color: '#475569',
                      bgcolor: 'white',
                      '&:hover': {
                        bgcolor: '#F8FAFC',
                        borderColor: '#CBD5E1',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  >
                    See How It Works
                  </Button>
                </Stack>

                {/* Social proof */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems="center"
                  spacing={2}
                  sx={{
                    pt: 2,
                    animation: `${slideUp} 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.9s both`,
                  }}
                >
                  <Box sx={{ display: 'flex' }}>
                    {[brand[400], '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B'].map((c, i) => (
                      <Avatar
                        key={i}
                        sx={{
                          width: 36, height: 36,
                          bgcolor: c,
                          border: '2.5px solid white',
                          ml: i > 0 ? -1.2 : 0,
                          fontSize: 13,
                          fontWeight: 700,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                      >
                        {['J', 'S', 'M', 'A', 'K'][i]}
                      </Avatar>
                    ))}
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, opacity: 0.3 }} />
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <StarRoundedIcon key={i} sx={{ fontSize: 18, color: '#F59E0B' }} />
                    ))}
                    <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ ml: 0.5 }}>
                      4.9/5 from 200+ reviews
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </FadeIn>
          </Box>

          {/* Hero visual — app screenshot mockup */}
          <FadeIn delay={400}>
            <Box sx={{
              mt: { xs: 8, md: 10 },
              position: 'relative',
              maxWidth: 1040,
              mx: 'auto',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -2,
                borderRadius: '20px',
                background: `linear-gradient(135deg, ${alpha(brand[400], 0.2)}, ${alpha('#7C3AED', 0.15)}, ${alpha('#3B82F6', 0.1)})`,
                backgroundSize: '200% 200%',
                animation: `${gradientShift} 6s ease infinite`,
                zIndex: -1,
                filter: 'blur(1px)',
              },
            }}>
              <Box
                sx={{
                  borderRadius: '18px',
                  border: '1px solid',
                  borderColor: alpha('#000', 0.06),
                  overflow: 'hidden',
                  boxShadow: `0 40px 80px -20px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.02)`,
                  bgcolor: 'white',
                }}
              >
                {/* Browser bar */}
                <Box sx={{
                  px: 3, py: 1.5, bgcolor: '#F8FAFC',
                  borderBottom: '1px solid', borderColor: alpha('#000', 0.04),
                  display: 'flex', alignItems: 'center', gap: 2,
                }}>
                  <Stack direction="row" spacing={0.75}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FCA5A5', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.2)' } }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FCD34D', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.2)' } }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#86EFAC', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.2)' } }} />
                  </Stack>
                  <Box sx={{
                    flex: 1, maxWidth: 400, mx: 'auto',
                    bgcolor: 'white', borderRadius: '8px',
                    border: '1px solid', borderColor: alpha('#000', 0.06),
                    px: 2, py: 0.5,
                    display: 'flex', alignItems: 'center', gap: 1,
                  }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10B981' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      app.podstack.co/podcasts
                    </Typography>
                  </Box>
                </Box>

                {/* App content mockup */}
                <Box sx={{ display: 'flex', minHeight: { xs: 300, md: 440 } }}>
                  {/* Sidebar */}
                  <Box sx={{
                    width: 220, borderRight: '1px solid', borderColor: '#F1F5F9',
                    bgcolor: 'white', display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', p: 2,
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '6px', border: '1px solid', borderColor: '#E2E8F0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <PodcastIcon size={16} color={brand[400]} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700}>Podstack</Typography>
                    </Stack>
                    <Stack spacing={0.5}>
                      {[
                        { label: 'Podcasts', active: true },
                        { label: 'Campaigns', active: false },
                        { label: 'Credits', active: false },
                      ].map((item) => (
                        <Box
                          key={item.label}
                          sx={{
                            px: 1.5, py: 1, borderRadius: '8px',
                            bgcolor: item.active ? alpha(brand[400], 0.08) : 'transparent',
                            color: item.active ? brand[500] : '#94A3B8',
                            fontWeight: item.active ? 600 : 500, fontSize: '0.825rem',
                            transition: 'all 0.2s',
                          }}
                        >
                          {item.label}
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  {/* Main area */}
                  <Box sx={{ flex: 1, bgcolor: '#F8FAFC', p: { xs: 2, md: 3 } }}>
                    {/* Search bar */}
                    <Box sx={{
                      bgcolor: 'white', borderRadius: '12px', border: '1px solid', borderColor: '#E2E8F0',
                      p: 2, mb: 2.5, display: 'flex', gap: 1.5, alignItems: 'center',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}>
                      <SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                      <Typography variant="body2" color="#94A3B8">
                        Search 4M+ podcasts by topic, audience, or host...
                      </Typography>
                    </Box>

                    {/* Filter chips */}
                    <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 0.5 }}>
                      {['Business', 'Technology', 'Marketing', '10k+ listeners'].map((tag) => (
                        <Box
                          key={tag}
                          sx={{
                            px: 1.5, py: 0.5, borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600,
                            bgcolor: tag.includes('listeners') ? alpha(brand[400], 0.1) : '#F1F5F9',
                            color: tag.includes('listeners') ? brand[500] : '#64748B',
                            border: tag.includes('listeners') ? `1px solid ${alpha(brand[400], 0.3)}` : '1px solid transparent',
                          }}
                        >
                          {tag}
                        </Box>
                      ))}
                    </Stack>

                    {/* Result cards */}
                    <Stack spacing={1.5}>
                      {[
                        { name: 'The Tim Ferriss Show', host: 'Tim Ferriss', rating: '4.8', listeners: '850K', gradient: 'linear-gradient(135deg, #1E293B 0%, #475569 100%)' },
                        { name: 'My First Million', host: 'Shaan Puri & Sam Parr', rating: '4.7', listeners: '320K', gradient: 'linear-gradient(135deg, #059669 0%, #34D399 100%)' },
                        { name: 'How I Built This', host: 'Guy Raz', rating: '4.9', listeners: '1.2M', gradient: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)' },
                      ].map((pod, i) => (
                        <Box
                          key={i}
                          sx={{
                            p: 2, bgcolor: 'white', borderRadius: '10px', border: '1px solid', borderColor: '#F1F5F9',
                            display: 'flex', gap: 2, alignItems: 'center',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                            transition: 'all 0.2s',
                            '&:hover': { borderColor: '#E2E8F0', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
                          }}
                        >
                          <Box sx={{ width: 48, height: 48, borderRadius: '10px', background: pod.gradient, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.825rem', lineHeight: 1.3 }}>{pod.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{pod.host}</Typography>
                          </Box>
                          <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Rating</Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>⭐ {pod.rating}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Listeners</Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>{pod.listeners}</Typography>
                            </Box>
                          </Stack>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600,
                              borderColor: '#E2E8F0', color: '#475569', minWidth: 'auto', px: 1.5,
                              '&:hover': { borderColor: brand[400], color: brand[500] },
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Box>
          </FadeIn>
        </Container>
      </Box>

      {/* ==================== LOGO STRIP / STATS ==================== */}
      <Box id="stats" sx={{
        borderTop: '1px solid', borderBottom: '1px solid', borderColor: alpha('#000', 0.04),
        bgcolor: 'white', py: { xs: 6, md: 8 },
      }}>
        <Container maxWidth="lg">
          <FadeIn>
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', mb: 5, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.1em' }}
            >
              The numbers behind the platform
            </Typography>
          </FadeIn>
          <Grid container spacing={4} justifyContent="center" alignItems="center">
            {[
              { value: '4', suffix: 'M+', label: 'Podcasts Indexed', icon: <TrendingUpIcon />, color: brand[400] },
              { value: '250', suffix: 'K+', label: 'Verified Emails', icon: <VerifiedIcon />, color: '#10B981' },
              { value: '99', suffix: '%', label: 'Email Accuracy', icon: <SpeedIcon />, color: '#7C3AED' },
              { value: '4000', suffix: '+', label: 'Active Users', icon: <AutoAwesomeIcon />, color: '#3B82F6' },
            ].map((stat, i) => (
              <Grid size={{ xs: 6, sm: 3 }} key={i} sx={{ textAlign: 'center' }}>
                <FadeIn delay={i * 100}>
                  <Stack spacing={1} alignItems="center">
                    <Box sx={{
                      width: 48, height: 48, borderRadius: '14px',
                      bgcolor: alpha(stat.color, 0.08),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: stat.color, fontSize: 24, mb: 0.5,
                    }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h3" fontWeight={800} sx={{
                      color: '#0F172A', letterSpacing: '-0.03em',
                      fontSize: { xs: '2rem', md: '2.5rem' },
                    }}>
                      <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{
                      textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.72rem',
                    }}>
                      {stat.label}
                    </Typography>
                  </Stack>
                </FadeIn>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ==================== FEATURES ==================== */}
      <Box id="features" sx={{ py: { xs: 10, md: 16 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <FadeIn>
            <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 6, md: 10 }, maxWidth: 700, mx: 'auto' }}>
              <Chip
                label="Features"
                size="small"
                sx={{
                  bgcolor: alpha(brand[400], 0.06),
                  color: brand[500],
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  border: `1px solid ${alpha(brand[400], 0.12)}`,
                }}
              />
              <Typography variant="h2" fontWeight={800} sx={{
                letterSpacing: '-0.03em', color: '#0F172A',
                fontSize: { xs: '2rem', md: '2.75rem' },
              }}>
                Everything you need to scale podcast outreach
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 600 }}>
                Stop wasting hours on manual research. Our platform automates discovery, verification, and outreach so you can focus on building relationships.
              </Typography>
            </Stack>
          </FadeIn>

          <Grid container spacing={3}>
            {[
              {
                icon: <SearchIcon sx={{ fontSize: 28 }} />,
                title: 'Smart Search',
                desc: 'Filter by topic, audience size, rating, region, and more. Find your perfect podcast match in seconds.',
                color: brand[400],
              },
              {
                icon: <EmailIcon sx={{ fontSize: 28 }} />,
                title: 'Verified Emails',
                desc: 'Get direct access to podcast hosts with 99% verified contact info. No more guessing or bounced emails.',
                color: '#10B981',
              },
              {
                icon: <AutoAwesomeIcon sx={{ fontSize: 28 }} />,
                title: 'Smart Outreach',
                desc: 'Send personalized pitches directly from the platform. Track opens, replies, and follow-ups in one place.',
                color: '#7C3AED',
              },
              {
                icon: <TrendingUpIcon sx={{ fontSize: 28 }} />,
                title: 'Audience Insights',
                desc: 'See listener estimates, Apple ratings, YouTube stats, and growth trends to prioritize high-impact shows.',
                color: '#3B82F6',
              },
              {
                icon: <VerifiedIcon sx={{ fontSize: 28 }} />,
                title: 'Regional Ratings',
                desc: 'View Apple Podcasts ratings across 7 regions to identify where shows have the strongest audiences.',
                color: '#F59E0B',
              },
              {
                icon: <SpeedIcon sx={{ fontSize: 28 }} />,
                title: 'Campaign Tracking',
                desc: 'Track your outreach pipeline from pitch to booking. Never lose track of a conversation again.',
                color: '#EF4444',
              },
            ].map((feature, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                <FadeIn delay={i * 80}>
                  <Box
                    sx={{
                      p: { xs: 3, md: 4 },
                      height: '100%',
                      borderRadius: '20px',
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: alpha('#000', 0.04),
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                      '&:hover': {
                        borderColor: alpha(feature.color, 0.25),
                        boxShadow: `0 20px 40px -12px ${alpha(feature.color, 0.12)}`,
                        transform: 'translateY(-6px)',
                        '& .feature-icon': {
                          transform: 'scale(1.1) rotate(-5deg)',
                        },
                      },
                    }}
                  >
                    <Box
                      className="feature-icon"
                      sx={{
                        width: 56, height: 56, borderRadius: '16px',
                        bgcolor: alpha(feature.color, 0.08),
                        color: feature.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mb: 3,
                        transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#1E293B', fontSize: '1.1rem' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                      {feature.desc}
                    </Typography>
                  </Box>
                </FadeIn>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ==================== HOW IT WORKS ==================== */}
      <Box sx={{
        py: { xs: 10, md: 16 },
        bgcolor: '#FAFAFA',
        position: 'relative',
      }}>
        <Container maxWidth="lg">
          <FadeIn>
            <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 6, md: 10 }, maxWidth: 600, mx: 'auto' }}>
              <Chip
                label="How It Works"
                size="small"
                sx={{
                  bgcolor: alpha(brand[400], 0.06), color: brand[500],
                  fontWeight: 700, fontSize: '0.8rem',
                  border: `1px solid ${alpha(brand[400], 0.12)}`,
                }}
              />
              <Typography variant="h2" fontWeight={800} sx={{
                letterSpacing: '-0.03em', color: '#0F172A',
                fontSize: { xs: '2rem', md: '2.75rem' },
              }}>
                Three steps to your next interview
              </Typography>
            </Stack>
          </FadeIn>

          <Grid container spacing={4} alignItems="stretch">
            {[
              {
                step: '01',
                title: 'Search',
                desc: 'Find podcasts that match your niche using our powerful filters and 4M+ show database.',
                icon: <SearchIcon sx={{ fontSize: 22 }} />,
                color: brand[400],
              },
              {
                step: '02',
                title: 'Connect',
                desc: 'Get verified host emails and send personalized pitches directly from the platform.',
                icon: <EmailIcon sx={{ fontSize: 22 }} />,
                color: '#10B981',
              },
              {
                step: '03',
                title: 'Book',
                desc: 'Track your outreach, follow up at the right time, and land your next interview.',
                icon: <StarRoundedIcon sx={{ fontSize: 22 }} />,
                color: '#7C3AED',
              },
            ].map((item, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={i}>
                <FadeIn delay={i * 150}>
                  <Box sx={{
                    p: { xs: 3.5, md: 4.5 },
                    bgcolor: 'white',
                    borderRadius: '24px',
                    height: '100%',
                    border: '1px solid',
                    borderColor: alpha('#000', 0.04),
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 20px 40px -12px rgba(0,0,0,0.08)',
                    },
                  }}>
                    {/* Large watermark number */}
                    <Typography sx={{
                      position: 'absolute', top: 12, right: 20,
                      fontSize: '5rem', fontWeight: 900,
                      color: alpha(item.color, 0.04),
                      lineHeight: 1, userSelect: 'none',
                    }}>
                      {item.step}
                    </Typography>

                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Box sx={{
                        width: 48, height: 48, borderRadius: '14px',
                        bgcolor: item.color, color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 4px 12px ${alpha(item.color, 0.3)}`,
                      }}>
                        {item.icon}
                      </Box>
                      <Typography variant="overline" sx={{
                        fontWeight: 800, fontSize: '0.7rem', color: '#94A3B8',
                        letterSpacing: '0.1em',
                      }}>
                        Step {item.step}
                      </Typography>
                    </Stack>

                    <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5, color: '#1E293B' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </FadeIn>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ==================== TESTIMONIALS ==================== */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <FadeIn>
            <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 6, md: 8 }, maxWidth: 600, mx: 'auto' }}>
              <Chip
                label="Testimonials"
                size="small"
                sx={{
                  bgcolor: alpha(brand[400], 0.06), color: brand[500],
                  fontWeight: 700, fontSize: '0.8rem',
                  border: `1px solid ${alpha(brand[400], 0.12)}`,
                }}
              />
              <Typography variant="h2" fontWeight={800} sx={{
                letterSpacing: '-0.03em', color: '#0F172A',
                fontSize: { xs: '2rem', md: '2.75rem' },
              }}>
                Loved by outreach professionals
              </Typography>
            </Stack>
          </FadeIn>

          <Box sx={{ maxWidth: 640, mx: 'auto' }}>
            <FadeIn>
              <Box sx={{
                p: { xs: 4, md: 5 },
                borderRadius: '24px',
                bgcolor: '#FAFAFA',
                border: '1px solid',
                borderColor: alpha('#000', 0.04),
                textAlign: 'center',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 32px -8px rgba(0,0,0,0.06)',
                },
              }}>
                <FormatQuoteIcon sx={{ fontSize: 40, color: alpha(brand[400], 0.2), mb: 2, transform: 'rotate(180deg)' }} />
                <Typography variant="h6" sx={{ lineHeight: 1.8, color: '#475569', mb: 4, fontStyle: 'italic', fontWeight: 400 }}>
                  "Podstack completely transformed our podcast outreach. We went from mass cold emails to hyper-targeted pitches. The verified contacts and audience data saved us hours every single week."
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} justifyContent="center">
                  <Avatar sx={{
                    width: 48, height: 48, bgcolor: brand[400],
                    fontWeight: 700, fontSize: 18,
                  }}>
                    A
                  </Avatar>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1E293B' }}>
                      Alice Draper
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hustling Writers
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </FadeIn>
          </Box>
        </Container>
      </Box>

      {/* ==================== PRICING ==================== */}
      <Box id="pricing" sx={{ py: { xs: 10, md: 16 }, bgcolor: '#FAFAFA' }}>
        <Container maxWidth="lg">
          <FadeIn>
            <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 6 }}>
              <Chip
                label="Pricing"
                size="small"
                sx={{
                  bgcolor: alpha(brand[400], 0.06), color: brand[500],
                  fontWeight: 700, fontSize: '0.8rem',
                  border: `1px solid ${alpha(brand[400], 0.12)}`,
                }}
              />
              <Typography variant="h2" fontWeight={800} sx={{
                letterSpacing: '-0.03em', color: '#0F172A',
                fontSize: { xs: '2rem', md: '2.75rem' },
              }}>
                Simple, transparent pricing
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                Start free. Upgrade as you grow. No hidden fees.
              </Typography>

              {/* Billing toggle */}
              <Box sx={{
                bgcolor: 'white', p: 0.5, borderRadius: '14px', mt: 2,
                border: '1px solid', borderColor: alpha('#000', 0.06),
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                <Stack direction="row" spacing={0.5}>
                  {(['monthly', 'yearly'] as const).map((cycle) => (
                    <Box
                      key={cycle}
                      onClick={() => setBillingCycle(cycle)}
                      sx={{
                        px: 3, py: 1, borderRadius: '10px', cursor: 'pointer',
                        bgcolor: billingCycle === cycle ? '#0F172A' : 'transparent',
                        color: billingCycle === cycle ? 'white' : '#94A3B8',
                        fontWeight: 600, fontSize: '0.875rem', textTransform: 'capitalize',
                        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                        userSelect: 'none',
                        display: 'flex', alignItems: 'center', gap: 1,
                      }}
                    >
                      {cycle}
                      {cycle === 'yearly' && (
                        <Box component="span" sx={{
                          fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: billingCycle === 'yearly' ? alpha('#fff', 0.2) : '#DCFCE7',
                          color: billingCycle === 'yearly' ? 'white' : '#16A34A',
                          px: 1, py: 0.25, borderRadius: '6px',
                        }}>
                          Save 17%
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </FadeIn>

          <Grid container spacing={3} alignItems="stretch" justifyContent="center">
            {[
              {
                title: 'Basic', subtitle: 'For getting started',
                monthlyPrice: 0, annualPrice: 0,
                features: ['20 email reveals / month', 'Basic search & filters', 'Email support'],
                highlight: false, popular: false,
              },
              {
                title: 'Pro', subtitle: 'For growing teams',
                monthlyPrice: 25, annualPrice: 250,
                features: ['200 email reveals / month', 'Advanced search & filters', 'Outreach tools', 'Priority support'],
                highlight: true, popular: true,
              },
              {
                title: 'Enterprise', subtitle: 'For power users',
                monthlyPrice: 200, annualPrice: 2000,
                features: ['1,000 email reveals / month', 'Advanced search & filters', 'Outreach tools', 'Dedicated account manager', 'Priority support'],
                highlight: false, popular: false,
              },
            ].map((tier, i) => {
              const price = billingCycle === 'yearly' ? tier.annualPrice : tier.monthlyPrice;
              return (
                <Grid size={{ xs: 12, md: 4 }} key={i}>
                  <FadeIn delay={i * 100}>
                    <Box sx={{
                      height: '100%', display: 'flex', flexDirection: 'column',
                      p: 4, bgcolor: tier.highlight ? alpha(brand[400], 0.03) : 'white', borderRadius: '24px',
                      border: tier.highlight ? '2px solid' : '1px solid',
                      borderColor: tier.highlight ? brand[400] : alpha('#000', 0.04),
                      boxShadow: tier.highlight
                        ? `0 24px 48px -12px ${alpha(brand[400], 0.15)}, 0 0 0 1px ${alpha(brand[400], 0.1)}`
                        : '0 1px 3px rgba(0,0,0,0.04)',
                      position: 'relative',
                      overflow: 'visible',
                      transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: tier.highlight
                          ? `0 32px 64px -16px ${alpha(brand[400], 0.2)}`
                          : '0 24px 48px -12px rgba(0,0,0,0.08)',
                      },
                    }}>
                      {/* Popular badge — positioned on top edge */}
                      {tier.popular && (
                        <Chip
                          icon={<StarRoundedIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                          label="Most Popular"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -14,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bgcolor: '#0F172A',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: 28,
                            boxShadow: '0 4px 12px rgba(15,23,42,0.25)',
                            '& .MuiChip-label': { color: 'white' },
                            '& .MuiChip-icon': { color: 'white' },
                          }}
                        />
                      )}

                      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, textAlign: 'center', color: '#1E293B', mt: tier.popular ? 2 : 0 }}>
                        {tier.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                        {tier.subtitle}
                      </Typography>

                      {/* Price */}
                      <Box sx={{ textAlign: 'center', mb: 4 }}>
                        {price === 0 ? (
                          <Typography variant="h3" fontWeight={800} sx={{ color: '#0F172A' }}>Free</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
                            <Typography variant="h3" fontWeight={800} sx={{ color: '#0F172A' }}>${price}</Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ ml: 0.5 }}>
                              /{billingCycle === 'yearly' ? 'yr' : 'mo'}
                            </Typography>
                          </Box>
                        )}
                        {billingCycle === 'yearly' && tier.monthlyPrice > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            ${(tier.annualPrice / 12).toFixed(2)}/mo billed annually
                          </Typography>
                        )}
                      </Box>

                      <Divider sx={{ mb: 3 }} />

                      <Stack spacing={2} sx={{ mb: 4, flexGrow: 1 }}>
                        {tier.features.map((feat, idx) => (
                          <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{
                              width: 20, height: 20, borderRadius: '6px',
                              bgcolor: alpha(tier.highlight ? brand[400] : '#94A3B8', 0.1),
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              <CheckRoundedIcon sx={{ fontSize: 14, color: tier.highlight ? brand[400] : '#94A3B8' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">{feat}</Typography>
                          </Stack>
                        ))}
                      </Stack>

                      <Button
                        fullWidth
                        variant={tier.highlight ? 'contained' : 'outlined'}
                        size="large"
                        onClick={() => handleNav('/signup')}
                        sx={{
                          mt: 'auto', py: 1.5, borderRadius: '14px', fontWeight: 700,
                          textTransform: 'none', fontSize: '0.95rem',
                          transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                          ...(tier.highlight
                            ? {
                              bgcolor: '#0F172A', color: 'white',
                              boxShadow: '0 4px 12px rgba(15,23,42,0.2)',
                              '&:hover': { bgcolor: '#1E293B', transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15,23,42,0.25)' },
                            }
                            : {
                              borderColor: '#E2E8F0', borderWidth: '1.5px', color: '#475569',
                              '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC', transform: 'translateY(-1px)' },
                            }),
                        }}
                      >
                        {price === 0 ? 'Get Started' : 'Start Free Trial'}
                      </Button>
                    </Box>
                  </FadeIn>
                </Grid>
              );
            })}
          </Grid>

          <FadeIn delay={300}>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 5 }}>
              All plans include a rolling monthly credit reset. Cancel or change plans anytime.
            </Typography>
          </FadeIn>
        </Container>
      </Box>

      {/* ==================== FINAL CTA ==================== */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: 'white' }}>
        <FadeIn>
          <Container maxWidth="md">
            <Box sx={{
              p: { xs: 5, md: 8 }, borderRadius: '28px',
              bgcolor: '#0F172A', color: 'white',
              textAlign: 'center', position: 'relative', overflow: 'hidden',
            }}>
              {/* Animated gradient mesh */}
              <Box sx={{
                position: 'absolute', inset: 0,
                background: `
                  radial-gradient(circle at 20% 50%, ${alpha(brand[400], 0.2)} 0%, transparent 50%),
                  radial-gradient(circle at 80% 50%, ${alpha('#7C3AED', 0.15)} 0%, transparent 50%),
                  radial-gradient(circle at 50% 0%, ${alpha('#3B82F6', 0.1)} 0%, transparent 60%)
                `,
                backgroundSize: '200% 200%',
                animation: `${gradientShift} 8s ease infinite`,
                pointerEvents: 'none',
              }} />

              {/* Grid pattern overlay */}
              <Box sx={{
                position: 'absolute', inset: 0,
                backgroundImage: `radial-gradient(${alpha('#fff', 0.03)} 1px, transparent 1px)`,
                backgroundSize: '24px 24px',
                pointerEvents: 'none',
              }} />

              <Stack spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h3" fontWeight={800} sx={{
                  letterSpacing: '-0.03em',
                  fontSize: { xs: '1.75rem', md: '2.75rem' },
                  lineHeight: 1.15,
                }}>
                  Ready to land your next{' '}
                  <Box component="span" sx={{
                    backgroundImage: `linear-gradient(90deg, ${brand[300]}, #A78BFA, ${brand[300]})`,
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: `${shimmer} 3s linear infinite`,
                  }}>
                    podcast interview
                  </Box>
                  ?
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.6, maxWidth: 500, lineHeight: 1.7 }}>
                  Join thousands of PR professionals and founders using Podstack to grow their brand through podcast guesting.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => handleNav('/signup')}
                  sx={{
                    bgcolor: 'white', color: '#0F172A',
                    py: 2, px: 5, fontSize: '1.05rem', fontWeight: 700,
                    borderRadius: '14px', textTransform: 'none',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                    '&:hover': {
                      bgcolor: '#F1F5F9',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 28px rgba(0,0,0,0.2)',
                    },
                  }}
                >
                  Start for Free
                </Button>
                <Typography variant="caption" sx={{ opacity: 0.4 }}>
                  No credit card required &middot; Free forever plan available
                </Typography>
              </Stack>
            </Box>
          </Container>
        </FadeIn>
      </Box>

      {/* ==================== FOOTER ==================== */}
      <Box sx={{ py: 5, bgcolor: '#FAFAFA', borderTop: '1px solid', borderColor: alpha('#000', 0.04) }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PodcastIcon size={22} color={brand[400]} />
              <Typography variant="body2" fontWeight={700} color="text.primary">
                Podstack
              </Typography>
            </Stack>
            <Stack direction="row" spacing={3}>
              {[
                { label: 'Terms', path: '/terms' },
                { label: 'Privacy', path: '/privacy' },
                { label: 'Contact', path: '/contact' },
              ].map((link) => (
                <Typography
                  key={link.label}
                  variant="body2"
                  color="text.secondary"
                  component={Link}
                  to={link.path}
                  sx={{
                    textDecoration: 'none',
                    transition: 'color 0.2s',
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Stack>
            <Typography variant="caption" color="text.disabled">
              &copy; {new Date().getFullYear()} Podstack Inc.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

// ===================== THEME WRAPPER =====================

export default function HomePage() {
  const lightTheme = React.useMemo(
    () =>
      createTheme({
        cssVariables: {
          colorSchemeSelector: 'data-mui-color-scheme',
          cssVarPrefix: 'template',
        },
        colorSchemes,
        typography,
        shadows,
        shape,
        components: {
          ...inputsCustomizations,
          ...dataDisplayCustomizations,
          ...feedbackCustomizations,
          ...navigationCustomizations,
          ...surfacesCustomizations,
        },
      }),
    []
  );

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <LandingPageContent />
    </ThemeProvider>
  );
}
